<template>
  <div>
    <breadcrumbs :breadcrumb-items="breadcrumbItems" />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CampaignToolbar
        :campaign="campaign"
        closing
      />
    </b-card>
    <CampaignNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4>
              {{ $t('stats.source_stats') }}:
              <a
                v-if="getDomainNameById(sourceId)"
                href="#"
                class="text-decoration-none text-primary"
                @click.prevent="openWebsite(getDomainNameById(sourceId))"
              >
                <em>{{ getDomainNameById(sourceId) }}</em>
              </a>
              <em v-else>{{ getDomainNameById(sourceId) }}</em>
              <feather-icon
                v-if="getDomainNameById(sourceId)"
                icon="ExternalLinkIcon"
                size="16"
                class="ml-25 cursor-pointer text-primary"
                @click="openWebsite(getDomainNameById(sourceId))"
              />
              <span
                class="cursor-pointer copy-id ml-25"
                @click="copyToClipboard(sourceId)"
              >
                [ID: {{ sourceId }}]
              </span>
            </h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="isLoading"
              @refresh="fetchSourceDetails"
              @input="toggleRange"
            />
          </div>
        </div>
      </div>

      <div
        v-if="isLoading"
        class="text-center my-3"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-table
        v-else-if="rows.length"
        :items="rows"
        :fields="fields"
        striped
        responsive
        small
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :sort-direction="sortDirection"
        show-empty
        foot-clone
        class="text-center"
        @sort-changed="onSortChanged"
      >
        <template #head(date)>
          {{ $t('stats.date') }}
        </template>
        <template #head(impressions)>
          {{ $t('stats.impressions') }}
        </template>
        <template #head(clicks)>
          {{ $t('stats.clicks') }}
        </template>
        <template #head(ctr)>
          CTR
        </template>
        <template #head(revenue)>
          {{ $t('stats.revenue') }}
        </template>
        <template #head(spent)>
          {{ $t('stats.spent') }}
        </template>
        <template #cell(date)="{ item }">
          {{ getFormattedDate(new Date(item.date)) }}
        </template>
        <template #cell(impressions)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(clicks)="data">
          {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
        </template>
        <template #cell(ctr)="data">
          {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
        </template>
        <template #cell(revenue)="data">
          {{ data.value ? currency('USD', data.value) : 0 }}
        </template>
        <template #cell(spent)="data">
          {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
        </template>
        <template #foot(date)="data">
          <strong :data-label="data.label">{{ $t('Records') }}: {{ rows.length }}</strong>
        </template>
        <template #foot(impressions)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('impressions')) }}
        </template>
        <template #foot(clicks)>
          {{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(sum('clicks')) }}
        </template>
        <template #foot(ctr)>
          {{ sum('clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(sum('clicks') / sum('impressions') * 100) : 0 }}%
        </template>
        <template #foot(revenue)>
          {{ currency('USD', sum('revenue')) }}
        </template>
        <template #foot(spent)>
          {{ currency(campaign.currency || 'USD', sum('spent')) }}
        </template>
      </b-table>
      <div
        v-else
        class="text-center text-muted my-3"
      >
        {{ $t('No data for this period') }}
      </div>
    </b-card>
  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CampaignToolbar from '@/views/dashboard/campaigns/CampaignToolbar.vue'
import CampaignNavigation from '@/views/dashboard/campaigns/CampaignNavigation.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import {
  BCard,
  BTable,
  BSpinner,
  VBTooltip,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import StatsDateRangeFilter from '@/views/dashboard/dsppartners/stats/StatsDateRangeFilter.vue'

export default {
  name: 'RtbStatsSource',
  components: {
    BCard,
    BTable,
    BSpinner,
    Breadcrumbs,
    UserToolbar,
    CampaignToolbar,
    CampaignNavigation,
    StatsDateRangeFilter,
    FeatherIcon,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      rangePicker: [getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)), getFormattedDate(new Date())],
      getFormattedDate,
      rows: [],
      sortBy: 'impressions',
      sortDesc: true,
      sortDirection: 'desc',
      fields: [
        { key: 'date', label: 'Дата', sortable: false },
        { key: 'impressions', label: 'Impressions', sortable: true },
        { key: 'clicks', label: 'Clicks', sortable: true },
        { key: 'ctr', label: 'CTR', sortable: true },
        { key: 'revenue', label: 'Revenue', sortable: true },
        { key: 'spent', label: 'Spent', sortable: false },
      ],
      breadcrumbItems: [],
      account: {},
      campaign: {},
      sourceId: '',
      isLoading: false,
      // Pagination
      currentPage: 1,
      totalRecords: 0,
      limitOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
    }
  },
  computed: {
    selectedRangeFormated() {
      let dateFrom
      let dateTo
      const selectedRange = this.rangePicker
      if (Array.isArray(selectedRange)) {
        [dateFrom, dateTo] = selectedRange
      } else {
        [dateFrom, , dateTo] = selectedRange.split(' ')
      }

      return {
        from: getFormattedDate(new Date(dateFrom)),
        to: dateTo === undefined ? getFormattedDate(new Date(dateFrom)) : getFormattedDate(new Date(dateTo)),
      }
    },
    getDomainNameById() {
      return siteId => this.$store.getters['dashboard-dsp-domains/domainNameByIdFromStore'](siteId)
    },
  },
  created() {
    this.sourceId = this.$route.params.sourceId
    this.getUser()
    this.getCampaignData()
    this.loadDomainName()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
    }
    // StatsDateRangeFilter will emit input event on initialization, which will trigger toggleRange
    // toggleRange will call fetchSourceDetails if dates changed
  },
  methods: {
    async getUser() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      this.setBreabcrumbs()
    },
    async loadDomainName() {
      const { sourceId, partnerId } = this.$route.params

      try {
        await this.$store.dispatch('dashboard-dsp-domains/fetchDomainNamesById', {
          dspId: partnerId,
          siteIds: [sourceId],
        })
      } catch (error) {
        // Error loading domain name
      }
    },
    openWebsite(domainName) {
      if (!domainName) return

      // Add protocol if missing
      let url = domainName
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`
      }

      // Open in new window
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ID copied to clipboard',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ID copied to clipboard',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      }
    },
    setBreabcrumbs() {
      const { sourceId } = this.$route.params
      this.breadcrumbItems = [
        {
          text: this.$t('Users'),
          to: '/users/',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}/`,
        },
        {
          text: this.$t('Campaigns'),
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}`,
        },
        {
          text: 'RTB Sources',
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/rtb-stats/sources`,
        },
        {
          text: this.getDomainNameById(sourceId),
          active: true,
        },
      ]
    },
    async fetchSourceDetails() {
      this.isLoading = true
      const { from, to } = this.selectedRangeFormated
      const { sourceId, id: campaignId, partnerId } = this.$route.params

      try {
        const response = await useJwt.getPhoenixDspSite({
          from,
          to,
          type: 'daily',
          site_id: sourceId,
          campaign_id: campaignId,
          partner_id: partnerId,
          limit: this.limit,
          sort_by: this.sortBy,
          sort_desc: this.sortDesc,
        })
        this.rows = (response.data.items || []).map(item => ({
          ...item,
        }))
      } catch (error) {
        this.rows = []
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message,
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoading = false
      }
    },
    sum(key) {
      return this.rows.reduce((acc, r) => acc + (r[key] || 0), 0)
    },
    formatDate(dateStr) {
      return new Date(dateStr).toLocaleDateString()
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        // Update URL without page reload
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.fetchSourceDetails()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    onSortChanged(ctx) {
      // Update sorting only for fields supported by backend
      const backendSortableFields = ['impressions', 'clicks', 'revenue']
      if (backendSortableFields.includes(ctx.sortBy)) {
        this.sortBy = ctx.sortBy
        this.sortDesc = ctx.sortDesc
        this.fetchSourceDetails()
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('rtbStatsSourceDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}

.copy-id {
  border-bottom: 1px dashed #ccc;
  transition: border-bottom-color 0.2s ease;
}

.copy-id:hover {
  border-bottom-color: #007bff;
}
</style>
